"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

import { useBrandDetails } from "@/contexts/BrandDetailsContext";
import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { cn } from "@/lib/cn";
import { SECTION_PADDING_X } from "@/styles/responsive-spacing";

// Type for normalized article (what we use internally)
interface Article {
  id: string;
  title: string;
  publication: string;
  date: string;
  summary: string;
  url?: string;
  thumbnail_url?: string;
}

interface ArticlesSectionProps {
  brandName: string;
}

// Article Card with Image (only rendered when article.thumbnail_url exists)
function ArticleCardWithImage({
  article,
  onImageError,
}: {
  article: Article;
  onImageError: (articleId: string) => void;
}) {
  return (
    <a
      href={article.url || "#"}
      target={article.url && article.url !== "#" ? "_blank" : undefined}
      rel={article.url && article.url !== "#" ? "noopener noreferrer" : undefined}
      data-testid="brand-article-card"
      className="group flex w-[75vw] flex-shrink-0 snap-start flex-col gap-3 rounded-2xl bg-white p-4 md:w-[340px] md:gap-4 md:p-5"
      onClick={article.url === "#" ? (e) => e.preventDefault() : undefined}
    >
      {/* Image Container - thumbnail_url is guaranteed to exist when this component is rendered */}
      <div className="h-[140px] w-full overflow-hidden rounded-xl bg-surface-muted md:h-[180px]">
        <img
          src={article.thumbnail_url}
          alt={article.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={() => onImageError(article.id)}
        />
      </div>

      {/* Content */}
      <div className="flex flex-col gap-2 md:gap-3">
        {/* Publication Badge and Date */}
        <div className="flex items-center justify-between">
          <span className="rounded-full bg-ink px-3 py-1 text-[10px] font-medium text-white">
            {article.publication}
          </span>
          <span className="text-caption text-ink-muted">{article.date}</span>
        </div>

        {/* Title */}
        <h3 className="text-body font-medium leading-snug text-ink">{article.title}</h3>

        {/* Summary */}
        <p className="line-clamp-2 text-caption leading-relaxed text-ink-secondary">
          {article.summary}
        </p>
      </div>

      {/* Read Article Link */}
      <div className="flex items-center gap-1.5">
        <span className="border-b border-ink text-caption font-medium text-ink">Read article</span>
        <ArrowRight className="h-3 w-3 text-ink" />
      </div>
    </a>
  );
}

// Article Card without Image
function ArticleCardNoImage({ article }: { article: Article }) {
  return (
    <a
      href={article.url || "#"}
      target={article.url && article.url !== "#" ? "_blank" : undefined}
      rel={article.url && article.url !== "#" ? "noopener noreferrer" : undefined}
      data-testid="brand-article-card"
      className="group flex w-[75vw] flex-shrink-0 snap-start flex-col justify-between gap-6 rounded-2xl bg-white p-4 md:w-[340px] md:gap-8 md:p-5"
      onClick={article.url === "#" ? (e) => e.preventDefault() : undefined}
    >
      {/* Content */}
      <div className="flex flex-1 flex-col justify-center gap-2 md:gap-3">
        {/* Publication Badge and Date */}
        <div className="flex items-center justify-between">
          <span className="rounded-full bg-ink px-3 py-1 text-[10px] font-medium text-white">
            {article.publication}
          </span>
          <span className="text-caption text-ink-muted">{article.date}</span>
        </div>

        {/* Title and Summary - with more spacing */}
        <div className="flex flex-col gap-2 pt-3 md:gap-3 md:pt-4">
          <h3 className="text-body font-medium leading-snug text-ink">{article.title}</h3>

          <p className="line-clamp-3 text-caption leading-relaxed text-ink-secondary">
            {article.summary}
          </p>
        </div>
      </div>

      {/* Read Article Link */}
      <div className="flex items-center gap-1.5">
        <span className="border-b border-ink text-caption font-medium text-ink">Read article</span>
        <ArrowRight className="h-3 w-3 text-ink" />
      </div>
    </a>
  );
}

export default function ArticlesSection({ brandName: _brandName }: ArticlesSectionProps) {
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());
  const { brandDetails, loading } = useBrandDetails();
  const articles = useMemo<Article[]>(
    () =>
      (brandDetails?.brand_articles ?? []).map((item) => ({
        ...item,
        id: item.url,
        thumbnail_url: item.thumbnail_url ?? undefined,
      })),
    [brandDetails?.brand_articles]
  );

  // Horizontal scroll navigation
  const {
    scrollRef: scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
    scrollToStart,
  } = useHorizontalScroll({
    threshold: 20, // Account for -mx-4 px-4 padding pattern
    viewportMultiplier: 0.85, // Scroll by ~one card width
    deps: [articles.length], // Recalculate when articles load
  });

  const handleImageError = useCallback((articleId: string) => {
    setBrokenImages((prev) => {
      const next = new Set(prev);
      next.add(articleId);
      return next;
    });
  }, []);

  useEffect(() => {
    setBrokenImages(new Set());
    scrollToStart();
  }, [articles, scrollToStart]);

  // Sort articles so those with working images appear first
  const sortedArticles = [...articles].sort((a, b) => {
    const aHasImage = !!a.thumbnail_url && !brokenImages.has(a.id);
    const bHasImage = !!b.thumbnail_url && !brokenImages.has(b.id);
    if (aHasImage === bHasImage) return 0;
    return aHasImage ? -1 : 1;
  });

  // Don't render anything while loading or if no articles
  if (loading || articles.length === 0) {
    return null;
  }

  return (
    <section
      className="mb-10 border-b border-gray-200 pb-16 md:mb-16 md:pb-20"
      data-testid="brand-learn-more-section"
    >
      {/* Header */}
      <div className="mb-8 flex items-center justify-between md:mb-10">
        <h2 className="text-title text-ink">Learn More</h2>

        {/* Navigation Arrows - only show when scrolling is possible */}
        {(canScrollLeft || canScrollRight) && (
          <div className="flex gap-2">
            <button
              onClick={scrollLeft}
              disabled={!canScrollLeft}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full bg-white transition-all",
                canScrollLeft
                  ? "text-ink hover:bg-surface-muted"
                  : "cursor-not-allowed text-ink-faint"
              )}
              aria-label="Previous articles"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={scrollRight}
              disabled={!canScrollRight}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full bg-white transition-all",
                canScrollRight
                  ? "text-ink hover:bg-surface-muted"
                  : "cursor-not-allowed text-ink-faint"
              )}
              aria-label="Next articles"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Articles - Horizontal scroll on mobile, grid on desktop */}
      <div
        ref={scrollContainerRef}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 md:gap-5"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          marginLeft: `calc(-1 * ${SECTION_PADDING_X.RESPONSIVE})`,
          marginRight: `calc(-1 * ${SECTION_PADDING_X.RESPONSIVE})`,
          paddingLeft: SECTION_PADDING_X.RESPONSIVE,
          paddingRight: SECTION_PADDING_X.RESPONSIVE,
          // Ensure snap points align with content padding
          scrollPaddingLeft: SECTION_PADDING_X.RESPONSIVE,
          scrollPaddingRight: SECTION_PADDING_X.RESPONSIVE,
        }}
      >
        {sortedArticles.map((article) => {
          const hasImage = !!article.thumbnail_url && !brokenImages.has(article.id);

          if (hasImage) {
            return (
              <ArticleCardWithImage
                key={article.id}
                article={article}
                onImageError={handleImageError}
              />
            );
          }

          return <ArticleCardNoImage key={article.id} article={article} />;
        })}
      </div>
    </section>
  );
}
